import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getHSNCategory,
    getHSNCodes,
    getHSNDetailsById,
    saveHSNDetails,
} from '../../api/PurchaseAPI/hsnCreationAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** HSN Category master (Category dropdown) */
export const fetchHSNCategoryList = createAsyncThunk(
    'hsnCreation/fetchHSNCategoryList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getHSNCategory();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load HSN categories');
        }
    }
);

/** All existing HSN/SAC codes — populates the Updation dropdown */
export const fetchHSNCodeList = createAsyncThunk(
    'hsnCreation/fetchHSNCodeList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getHSNCodes();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load HSN codes');
        }
    }
);

/** Full row for a selected HSN code — used to prefill the form on Updation */
export const fetchHSNDetails = createAsyncThunk(
    'hsnCreation/fetchHSNDetails',
    async (id, { rejectWithValue }) => {
        try {
            const res = await getHSNDetailsById(id);
            const rows = res.data?.Data || [];
            return rows[0] || null;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load HSN details');
        }
    }
);

/** Save (Creation) or resubmit (Updation) an HSN/SAC code */
export const submitHSNDetails = createAsyncThunk(
    'hsnCreation/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveHSNDetails(payload);
            const status = res.data?.Data;
            if (status !== 'Successfull') {
                return rejectWithValue(status || 'Failed to save HSN details');
            }
            return status;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save HSN details');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    categoryList: [],
    codeList: [],
    details: null,
    saveResult: null,
    loading: {
        categoryList: false,
        codeList: false,
        details: false,
        save: false,
    },
    errors: {
        categoryList: null,
        codeList: null,
        details: null,
        save: null,
    },
};

const hsnCreationSlice = createSlice({
    name: 'hsnCreation',
    initialState,
    reducers: {
        clearHSNDetails:    (s) => { s.details = null; s.errors.details = null; },
        clearHSNSaveResult: (s) => { s.saveResult = null; s.errors.save = null; },
        resetHSNCreation:   () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHSNCategoryList.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchHSNCategoryList.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchHSNCategoryList.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchHSNCodeList.pending,   (s) => { s.loading.codeList = true; s.errors.codeList = null; })
            .addCase(fetchHSNCodeList.fulfilled, (s, a) => { s.loading.codeList = false; s.codeList = a.payload; })
            .addCase(fetchHSNCodeList.rejected,  (s, a) => { s.loading.codeList = false; s.errors.codeList = a.payload; });

        builder
            .addCase(fetchHSNDetails.pending,   (s) => { s.loading.details = true; s.errors.details = null; s.details = null; })
            .addCase(fetchHSNDetails.fulfilled, (s, a) => { s.loading.details = false; s.details = a.payload; })
            .addCase(fetchHSNDetails.rejected,  (s, a) => { s.loading.details = false; s.errors.details = a.payload; });

        builder
            .addCase(submitHSNDetails.pending,   (s) => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitHSNDetails.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitHSNDetails.rejected,  (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearHSNDetails,
    clearHSNSaveResult,
    resetHSNCreation,
} = hsnCreationSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectHSNCategoryList = (s) => s.hsnCreation.categoryList;
export const selectHSNCodeList     = (s) => s.hsnCreation.codeList;
export const selectHSNDetails      = (s) => s.hsnCreation.details;
export const selectHSNSaveResult   = (s) => s.hsnCreation.saveResult;

export const selectHSNCategoryListLoading = (s) => s.hsnCreation.loading.categoryList;
export const selectHSNCodeListLoading     = (s) => s.hsnCreation.loading.codeList;
export const selectHSNDetailsLoading      = (s) => s.hsnCreation.loading.details;
export const selectHSNSaveLoading         = (s) => s.hsnCreation.loading.save;
export const selectHSNSaveError           = (s) => s.hsnCreation.errors.save;

export default hsnCreationSlice.reducer;
